import { Divider, Flex, Spacing, Text } from '@billy/design-system'
import { forwardRef, ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useAttachmentsUploadManager } from './contexts/attachmentsUploadManagerContext'
import { AttachmentsList } from './elements/AttachmentsList'
import * as Styled from './styles'

interface AttachmentsSectionProps {
  onAttachmentsUpload?: (attachmentUuid: string[]) => void
  onCancel?: () => void
  onRemove?: () => void
  onRetry?: () => void
}

export const AttachmentsSection = forwardRef<HTMLDivElement, AttachmentsSectionProps>(
  ({ onAttachmentsUpload, onCancel, onRemove, onRetry }, ref): ReactElement | null => {
    const { t } = useTranslation()
    const { attachments, attachmentUuids, cancelUpload, removeAttachment, retryUpload } = useAttachmentsUploadManager()

    const handleCancelUpload = useCallback(
      (id: string) => {
        cancelUpload(id)
        onCancel?.()
      },
      [cancelUpload, onCancel],
    )

    const handleRemoveAttachment = useCallback(
      (id: string) => {
        removeAttachment(id)
        onRemove?.()
      },
      [onRemove, removeAttachment],
    )

    const handleRetryUpload = useCallback(
      (id: string) => {
        retryUpload(id)
        onRetry?.()
      },
      [onRetry, retryUpload],
    )

    useEffect(() => {
      onAttachmentsUpload?.(attachmentUuids)
    }, [attachmentUuids, onAttachmentsUpload])

    if (!attachments.length) {
      return null
    }

    return (
      <Styled.AttachmentsSectionWrapper ref={ref}>
        <Flex flexDirection="column" gap={Spacing.S8}>
          <Divider />
          <Text colorVariant="secondary">{t('attachments_section.title')}</Text>
          <AttachmentsList
            attachments={attachments}
            onCancelUpload={handleCancelUpload}
            onRemoveAttachment={handleRemoveAttachment}
            onRetryUpload={handleRetryUpload}
          />
        </Flex>
      </Styled.AttachmentsSectionWrapper>
    )
  },
)
